import { reactive, ref, watch } from 'vue';

// How a reader has chosen to look at one time series chart, remembered per
// browser: per period or cumulative, and which series they have hidden or set
// beside their previous-period twin. Only the departures from the defaults are
// stored, keyed by series key, so a series added later starts visible, and a
// reload of the data, whose series keep their keys, keeps the choices too.
//
// Without a storage key the settings live only as long as the page.
export default function useChartViewSettings(storageKey) {
  function load() {
    if (!storageKey) return {};
    try {
      const parsed = JSON.parse(localStorage.getItem(storageKey) || '{}');
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }

  function keyList(value) {
    return Array.isArray(value) ? value.filter((key) => typeof key === 'string') : [];
  }

  function flags(keys) {
    return Object.fromEntries(keys.map((key) => [key, true]));
  }

  const stored = load();

  const cumulative = ref(stored.cumulative === true);
  const hidden = reactive(flags(keyList(stored.hidden)));
  const compared = reactive(flags(keyList(stored.compared)));

  function onKeys(entries) {
    return Object.keys(entries).filter((key) => entries[key] === true);
  }

  function save() {
    if (!storageKey) return;
    try {
      const settings = {
        cumulative: cumulative.value,
        hidden: onKeys(hidden),
        compared: onKeys(compared)
      };
      localStorage.setItem(storageKey, JSON.stringify(settings));
    } catch {
      // Storage can be unavailable (private window, blocked site data). The
      // choice then simply lasts until the page is reloaded.
    }
  }

  watch([cumulative, hidden, compared], save, { deep: true });

  return { cumulative, hidden, compared };
}
